/* eslint-disable max-lines */
import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { PublicError } from '@modernloop/shared/helper-components';
import { EyeCrossedIcon, RsvpDeclineLeftIcon, RsvpGoingLeftIcon, RsvpMaybeLeftIcon } from '@modernloop/shared/icons';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Avatar, ListItemIcon, ListItemText, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  AdjustInterviewerRsvp_InterviewFragment,
  DeclineReason,
  ResponseStatus,
  useInterviewAdjustRsvpMutation,
} from 'src/generated/mloop-graphql';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { renderInterviewerHours } from 'src/utils/renderTimeRange';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import {
  DeclineReasons,
  getDeclineReasonLabel,
} from 'src/views-new/MetricsPage/InterviewersOverview/Charts/DeclineReason';

interface Props {
  interviewerId: string;
  onClose: () => void;
}

interface Fragments {
  interview: AdjustInterviewerRsvp_InterviewFragment;
}

const AdjustInterviewerRsvp: FCWithFragments<Fragments, Props> = ({ interview, interviewerId, onClose }) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  const { enqueueSnackbar } = useSnackbar();
  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();
  const [adjustInterviewerRsvp] = useInterviewAdjustRsvpMutation();
  const [declineReasonError, setDeclineReasonError] = useState('');
  const [declineCommentError, setDeclineCommentError] = useState('');
  const interviewer = interview?.applicationStageInterviewInterviewers?.find((i) => i.interviewerId === interviewerId);
  const [selectedResponseStatus, setSelectedResponseStatus] = useState(interviewer?.responseStatus);
  const [showDeclineReason, setShowDeclineReason] = useState(interviewer?.responseStatus === ResponseStatus.Declined);
  const [declineReason, setDeclineReason] = useState(
    interviewer?.declines ? interviewer?.declines[0]?.reason?.toLowerCase() : ''
  );
  const [declineComment, setDeclineComment] = useState(interviewer?.declines ? interviewer?.declines[0]?.comment : '');

  if (!interviewer) {
    return null;
  }
  return (
    <Dialog
      dataTestId="adjust-interviewer-rsvp-dialog"
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/scheduling/edit_64.png"
          alt={t('schedule.content.adjustRsvpAlt')}
        />
      }
      open
      onClose={onClose}
      title={t('schedule.content.adjustInterviewerRsvp')}
      subTitle={t('schedule.content.adjustRsvpSubtitle')}
      cancelOptions={{
        onClick: onClose,
        label: t('hiring-dashboard-common:common.cancel'),
      }}
      submitOptions={{
        onClick: () => {
          if (declineReason === '' && selectedResponseStatus === ResponseStatus.Declined) {
            setDeclineReasonError(t('schedule.content.pleaseSelectDeclineReason'));
            return;
          }
          if (
            declineComment === '' &&
            selectedResponseStatus === ResponseStatus.Declined &&
            declineReason === DeclineReason.Other.toLowerCase()
          ) {
            setDeclineCommentError(t('schedule.content.pleaseAddNote'));
            return;
          }
          adjustInterviewerRsvp({
            variables: {
              applicationStageInterviewId: interview.id,
              interviewerId,
              responseStatus: selectedResponseStatus as ResponseStatus,
              reason:
                selectedResponseStatus !== ResponseStatus.Declined
                  ? null
                  : (declineReason?.toUpperCase() as DeclineReason),
              comment: declineComment,
            },
          }) // eslint-disable-next-line promise/always-return
            .then(() => {
              refetchScheduleTaskQuery();
              refetchActivityFeed();
              enqueueSnackbar(t('schedule.content.rsvpAdjustedSuccessfully'), { variant: 'success' });
            })
            .catch((error) => {
              enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
            });
          onClose();
        },
        label: t('hiring-dashboard-common:common.save'),
      }}
    >
      <Stack direction="column" spacing={1}>
        <Stack direction="column">
          <Typography variant="subtitle1">{interview.name}</Typography>

          {interview.isHiddenFromCandidate && (
            <Stack direction="row" alignItems="center">
              <EyeCrossedIcon />
              <Typography variant="body2" color="text.secondary">
                {t('schedule.content.hidden')}
              </Typography>
            </Stack>
          )}

          <Typography variant="body1" color="text.secondary">
            {renderInterviewerHours(interview.startAt, interview.endAt, getLocalTimezone(), true, false)}
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="left"
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            key={interviewer?.interviewerId}
            spacing={1}
            justifyContent="space-between"
          >
            <Avatar
              src={interviewer?.interviewer?.internalProfilePictureUrl ?? ''}
              alt={interviewer?.interviewer?.fullName || ''}
            />
            <Typography variant="body1" noWrap minWidth="216px">
              {interviewer?.interviewer?.fullName || ''}
            </Typography>
          </Stack>
          <Select
            fullWidth
            value={selectedResponseStatus}
            onChange={(val) => {
              setShowDeclineReason(val.target.value === ResponseStatus.Declined);
              setSelectedResponseStatus(val.target.value as ResponseStatus);
              if (val.target.value !== ResponseStatus.Declined) {
                setDeclineComment('');
              }
            }}
            renderValue={(value) => {
              if (value === ResponseStatus.NeedsAction) {
                return (
                  <Stack gap={1} alignItems="center" direction="row">
                    <Typography variant="body1" color="text.secondary">
                      {t('schedule.content.selectAResponse')}
                    </Typography>
                  </Stack>
                );
              }

              if (value === ResponseStatus.Accepted) {
                return (
                  <Stack gap={1} alignItems="center" direction="row">
                    <RsvpGoingLeftIcon />
                    {t('schedule.content.accept')}
                  </Stack>
                );
              }

              if (value === ResponseStatus.Declined) {
                return (
                  <Stack gap={1} alignItems="center" direction="row">
                    <RsvpDeclineLeftIcon />
                    {t('schedule.content.decline')}
                  </Stack>
                );
              }
              if (value === ResponseStatus.Tentative) {
                return (
                  <Stack gap={1} alignItems="center" direction="row">
                    <RsvpMaybeLeftIcon />
                    {t('schedule.content.maybe')}
                  </Stack>
                );
              }

              return null;
            }}
          >
            <MenuItem value={ResponseStatus.Accepted}>
              <ListItemIcon>
                <RsvpGoingLeftIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('schedule.content.accept')}
                secondary={
                  interviewer?.responseStatus === ResponseStatus.Accepted ? t('schedule.content.currentRsvpStatus') : ''
                }
              />
            </MenuItem>

            <MenuItem value={ResponseStatus.Declined}>
              <ListItemIcon>
                <RsvpDeclineLeftIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('schedule.content.decline')}
                secondary={
                  interviewer?.responseStatus === ResponseStatus.Declined ? t('schedule.content.currentRsvpStatus') : ''
                }
              />
            </MenuItem>

            <MenuItem value={ResponseStatus.Tentative}>
              <ListItemIcon>
                <RsvpMaybeLeftIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('schedule.content.maybe')}
                secondary={
                  interviewer?.responseStatus === ResponseStatus.Tentative
                    ? t('schedule.content.currentRsvpStatus')
                    : ''
                }
              />
            </MenuItem>
          </Select>
        </Stack>
        {showDeclineReason && (
          <Stack direction="column" spacing={1}>
            <Select
              error={declineReasonError !== ''}
              displayEmpty
              value={declineReason}
              onChange={(val) => {
                setDeclineReason(val.target.value);
                setDeclineReasonError('');
              }}
              renderValue={(value) => {
                if (value === '') {
                  return (
                    <Typography variant="body1" color="text.secondary">
                      {t('schedule.content.reason')}
                    </Typography>
                  );
                }
                return getDeclineReasonLabel(t, value);
              }}
            >
              {Object.keys(DeclineReasons)
                .filter((key) => key !== 'missed')
                .map((key) => (
                  <MenuItem key={key} value={key}>
                    {getDeclineReasonLabel(t, key)}
                  </MenuItem>
                ))}
            </Select>
            {declineReasonError !== '' && <PublicError error={declineReasonError} />}
            <TextField
              placeholder={t('schedule.content.note')}
              onChange={(val) => {
                setDeclineComment(val.target.value);
                setDeclineCommentError('');
              }}
              value={declineComment}
              fullWidth
              multiline
              rows={3}
              error={declineCommentError !== ''}
            />
            {declineCommentError !== '' && <PublicError error={declineCommentError} />}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InterviewAdjustRsvpMutation = gql`
  mutation interviewAdjustRsvp(
    $applicationStageInterviewId: uuid!
    $interviewerId: uuid!
    $responseStatus: ResponseStatus!
    $reason: DeclineReason
    $comment: String
  ) {
    applicationStageInterviewInterviewerUpdate(
      input: {
        applicationStageInterviewId: $applicationStageInterviewId
        interviewerId: $interviewerId
        rsvp: { reason: $reason, comment: $comment, responseStatus: $responseStatus }
      }
    ) {
      result
    }
  }
`;

AdjustInterviewerRsvp.fragments = {
  interview: gql`
    fragment AdjustInterviewerRsvp_interview on ApplicationStageInterview {
      id
      startAt
      endAt
      name
      isHiddenFromCandidate
      applicationStageInterviewInterviewers {
        interviewerId
        role
        responseStatus
        isOptional
        declines {
          reason
          comment
        }
        interviewer {
          id
          internalProfilePictureUrl
          fullName
          givenName
        }
      }
    }
  `,
};

export default AdjustInterviewerRsvp;
